import { useEffect, useState } from 'react';
import { styled, useTheme } from '@mui/material';
import { Typography } from '../../atoms/Typography/Typography';
import { Icon } from '../../atoms/Icon/Icon';

export interface ToastProps {
    heading: string;
    type: 'error' | 'confirm' | 'alert' | 'warning';
    messages?: [] | Array<string>;
    disabled?: boolean;
    open?: boolean;
    onClose?: () => void;
    isTimer?: boolean;
    disappearTime?: number;
    handleClose?: () => void;
    handleTryAgain?: () => void;
}

export interface MainDivProps {
    color: string;
    disabled: boolean;
    isSuccess: boolean;
}

export interface ToastTextProps {
    disabled: boolean;
    isSuccess: boolean;
}

export interface DisabledProps {
    disabled: boolean;
}

const StyledMainDiv = styled('div')<MainDivProps>(({ theme, isSuccess }) => ({
    display: 'flex',
    padding: theme.spacing(2),
    gap: '8px',
    borderRadius: '6px',
    background: isSuccess ? theme.palette.tertiary[50] : theme.palette.semantic.bg.inverse,
    width: '392px',
    boxShadow: '0px 4px 10px 0px rgba(0, 0, 0, 0.05)',
    position: 'fixed',
    left: 0,
    bottom: 0,
    zIndex: 1300,
    alignItems: 'center'
}));

const StyledContentDiv = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    width: '80%'
});

const StyledCloseDiv = styled('div')({
    display: 'flex',
    cursor: 'pointer',
    justifyContent: 'flex-start'
});

const StyledIconDiv = styled('div')({
    display: 'flex',
    width: '32px',
    padding: '1px 2.3px 1.2px 2px',
    justifyContent: 'center',
    alignItems: 'center'
});

const StyledHeader = styled(Typography)<ToastTextProps>(({ disabled, isSuccess, theme }) => ({
    fontSize: theme.spacing(2),
    fontWeight: 500,
    color: disabled
        ? theme.palette.semantic.text.muted
        : isSuccess
        ? theme.palette.neutral[950]
        : theme.palette.semantic.text.inverse,
    lineHeight: theme.spacing(2.4)
}));

const StyledContent = styled(Typography)<ToastTextProps>(({ disabled, isSuccess, theme }) => ({
    fontSize: '13px',
    fontWeight: 400,
    color: disabled
        ? theme.palette.semantic.text.muted
        : isSuccess
        ? theme.palette.neutral[950]
        : theme.palette.semantic.text.inverse,
    lineHeight: theme.spacing(2),
    letterSpacing: '0.13px'
}));

const StyledTryAgain = styled(Typography)<DisabledProps>(({ disabled, theme }) => ({
    fontSize: '13px',
    fontWeight: 400,
    color: disabled ? theme.palette.semantic.text.muted : theme.palette.semantic.int.link,
    lineHeight: theme.spacing(2),
    letterSpacing: '0.13px',
    cursor: disabled ? 'not-allowed' : 'pointer'
}));

type ToastType = ToastProps['type'];

const ICON_BY_TYPE: Record<ToastType, string> = {
    error: 'error',
    confirm: 'check-circle',
    alert: 'alert',
    warning: 'warning'
};

export const Toast = ({
    heading,
    type,
    messages = [],
    open,
    onClose,
    handleClose,
    handleTryAgain,
    disabled = false,
    isTimer = false,
    disappearTime = 5
}: ToastProps) => {
    const theme = useTheme();
    const isControlled = typeof open === 'boolean';
    const [internalOpen, setInternalOpen] = useState(true);
    const isShown = isControlled ? Boolean(open) : internalOpen;

    const closeToast = () => {
        onClose?.();
        handleClose?.();
        if (!isControlled) {
            setInternalOpen(false);
        }
    };

    useEffect(() => {
        if (!isTimer || !isShown) {
            return;
        }

        const timeout = setTimeout(() => {
            closeToast();
        }, disappearTime * 1000);

        return () => clearTimeout(timeout);
    }, [isTimer, isShown, disappearTime, onClose, handleClose, isControlled]);

    useEffect(() => {
        if (!isControlled) {
            setInternalOpen(true);
        }
    }, [heading, type, isControlled]);

    const colorMap = (type: ToastType) => {
        let color = theme.palette.semantic.bd.error;
        switch (type) {
            case 'warning':
                color = theme.palette.semantic.bd.warning;
                break;
            case 'confirm':
                color = theme.palette.tertiary[600];
                break;
            case 'alert':
                color = theme.palette.semantic.bd.info;
                break;
        }
        return color;
    };
    const isSuccess = type === 'confirm';

    return (
        <div>
            {isShown && (
                <StyledMainDiv color={colorMap(type)} disabled={disabled} isSuccess={isSuccess}>
                    <StyledIconDiv>
                        <Icon
                            id={ICON_BY_TYPE[type]}
                            size={16}
                            fill={disabled ? theme.palette.semantic.text.muted : colorMap(type)}
                        />
                    </StyledIconDiv>
                    <StyledContentDiv>
                        <StyledHeader label={heading} disabled={disabled} isSuccess={isSuccess} />
                        {messages.length > 0 && (
                            <>
                                {messages.map((message) => {
                                    return (
                                        <StyledContent
                                            key={message}
                                            label={message}
                                            disabled={disabled}
                                            isSuccess={isSuccess}
                                        />
                                    );
                                })}
                                {handleTryAgain && (
                                    <StyledTryAgain onClick={handleTryAgain} label="Try again" disabled={disabled} />
                                )}
                            </>
                        )}
                    </StyledContentDiv>
                    <StyledCloseDiv onClick={closeToast}>
                        <Icon
                            id="close"
                            size={16}
                            fill={
                                disabled
                                    ? theme.palette.semantic.text.muted
                                    : isSuccess
                                    ? theme.palette.neutral[950]
                                    : theme.palette.semantic.text.inverse
                            }
                        />
                    </StyledCloseDiv>
                </StyledMainDiv>
            )}
        </div>
    );
};
